import Description from '@tebuto/forms/Description'
import Suffix from '@tebuto/forms/Suffix'
import { cn } from '@tebuto/lib/utils'
import { Field, useFormikContext } from 'formik'
import React, { ChangeEvent, type JSX, useEffect, useRef } from 'react'
import ErrorMessage from './ErrorMessage'
import { Label } from './Label'
import { useFieldErrorVisible } from './useFieldErrorVisible'

export interface FormTextAreaProps {
    name: string
    label?: string
    autoComplete?: string
    type?: string
    placeholder?: string
    className?: string
    suffix?: string
    mandatory?: boolean
    description?: string
    rows?: number
    customOnChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void
    readOnly?: boolean
    'data-testid'?: string
}

export default function FormTextArea({
    className = '',
    label,
    name,
    autoComplete,
    suffix,
    description,
    mandatory,
    rows = 3,
    placeholder,
    customOnChange,
    readOnly,
    'data-testid': testId,
    ...rest
}: FormTextAreaProps & React.HTMLAttributes<HTMLDivElement>): JSX.Element {
    const { setFieldValue } = useFormikContext()
    const showFieldError = useFieldErrorVisible(name)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    useEffect(() => {
        const textarea = textareaRef.current
        if (textarea) {
            const adjustHeight = () => {
                textarea.style.height = 'auto'
                textarea.style.height = `${textarea.scrollHeight + 10}px`
            }

            adjustHeight() // Adjust height on initial render
            textarea.addEventListener('input', adjustHeight)
            return () => textarea.removeEventListener('input', adjustHeight)
        }
    }, [])

    function CSSClasses() {
        let defaultCSS =
            'flex-1 min-w-0 block w-full px-3 py-2 border text-foreground-secondary placeholder:text-foreground-subtle border-secondary rounded-lg shadow-xs focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm '
        if (showFieldError) defaultCSS += ' border-red-500 ring-red-500!'
        if (suffix) defaultCSS += ' rounded-none rounded-l-md border-r-0 '
        return defaultCSS
    }

    return (
        <div className={cn('relative', className)} {...rest}>
            {label && (
                <div className="flex-col mb-3">
                    <Label htmlFor={name} mandatory={mandatory}>
                        {label}
                    </Label>
                </div>
            )}
            <div className="mt-1 flex rounded-md shadow-xs mb-2">
                <Field
                    as="textarea"
                    id={name}
                    data-testid={testId ?? `field-${name}`}
                    name={name}
                    rows={rows}
                    placeholder={placeholder}
                    autoComplete={autoComplete}
                    readOnly={readOnly}
                    disabled={readOnly}
                    className={CSSClasses()}
                    innerRef={textareaRef}
                    {...(customOnChange
                        ? {
                              onChange: (event: ChangeEvent<HTMLTextAreaElement>) => {
                                  setFieldValue(name, event.target.value, true)
                                  customOnChange(event)
                              }
                          }
                        : {})}
                />
                <Suffix suffix={suffix} />
            </div>
            <ErrorMessage name={name} />
            <Description description={description} />
        </div>
    )
}
